import { ActionIcon, Badge, Button, Stack, Tooltip } from "@mantine/core";
import { GridColDef } from "@mui/x-data-grid";
import { ClipboardTextIcon, PencilLineIcon, PlusIcon, TrashIcon } from "@phosphor-icons/react";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useMemo } from "react";
import z from "zod";

import { DataGridBase, useDataGridBaseParams } from "@/components/DataGridBase";
import { RoleGuard } from "@/components/RoleGuard";
import { DashboardComponents } from "@/features/dashboard/_components/dashboard";
import { useSession } from "@/hooks";
import { MODAL_HANDLER } from "@/lib/Mantine";
import { Role, useListOrdersInfiniteQuery } from "@/store";
import { requireRoles } from "@/utils/requiredRoles";

export type OrderSearchParams = z.infer<typeof orderSearchParamsSchema>;
export const orderSearchParamsSchema = z
  .object({
    items: z
      .array(
        z.object({
          id: z.number().or(z.string()).optional(),
          field: z.string(),
          operator: z.string(),
          value: z.any().optional()
        })
      )
      .default([]),
    quickFilterValues: z.array(z.any()).default([]),
    settings: z
      .object({
        pagination: z.object({ pageSize: z.number().min(1), page: z.number().min(1) }),
        density: z.enum(["comfortable", "compact", "standard"]),
        columnVisibility: z.record(z.string(), z.boolean())
      })
      .partial()
  })
  .partial();

export const Route = createFileRoute("/_private/dashboard/_base/pedidos/")({
  component: RouteComponent,
  beforeLoad: requireRoles({ allowed: [Role.Administrador, Role.Transportadora, Role.Usuario] })
});

function RouteComponent() {
  const [queryState] = useDataGridBaseParams();
  const { id, role } = useSession();
  const navigate = useNavigate();

  const { data, fetchNextPage, refetch, hasNextPage, isFetching } = useListOrdersInfiniteQuery(
    {
      id,
      params: {
        filter: queryState?.quickFilterValues?.join(" ") ?? "",
        pageSize: 50
      }
    },
    { skip: !id }
  );
  const rows = data?.pages.flatMap((page) => page.data) || [];

  //TODO company e scheduling está em falta no backend
  const columns: GridColDef<(typeof rows)[number]>[] = useMemo(
    () => [
      {
        field: "id",
        headerName: "ID",
        type: "number",
        align: "center",
        headerAlign: "center",
        width: 70
      },
      {
        field: "code",
        headerName: "Código",
        type: "string"
      },
      {
        field: "customer",
        headerName: "Cliente",
        valueGetter: (params, row) => row.customer?.name,
        flex: 1,
        minWidth: 150,
        type: "string"
      },
      {
        field: "createAt",
        headerName: "Data",
        width: 100,
        type: "string"
      },
      {
        field: "qtyOrdered",
        headerName: "Qtd. Pedida",
        width: 120,
        align: "right",
        headerAlign: "right"
      },
      {
        field: "qtyTransported",
        headerName: "Qtd. Entregue",
        width: 120,
        align: "right",
        headerAlign: "right"
      },
      {
        field: "status",
        headerName: "Status",
        width: 100,
        align: "center",
        headerAlign: "center",
        type: "string"
      },
      {
        field: "enable",
        headerName: "Ativo",
        width: 100,
        type: "actions",
        renderCell: ({ value }) => (
          <Badge color={value ? "green" : "red"} variant="light" size="sm">
            {value ? "Ativo" : "Inativo"}
          </Badge>
        )
      },
      {
        field: "actions",
        headerName: "Ações",
        width: 120,
        type: "actions",
        renderCell: ({ row }) => {
          const { qtyOrdered, qtyTransported, id, status } = row;

          const orderedQueueSameTransported = qtyOrdered !== qtyTransported;
          const normalizeStatus = (status: string) => Number(status.replace("%", ""));
          const canDelete = orderedQueueSameTransported || normalizeStatus(status) === 100;

          // if (normalizeStatus(status) === 100) return null;
          // if (normalizeStatus(status) >= 100) return null;

          return (
            <ActionIcon.Group>
              <Tooltip label="Carregamentos">
                <ActionIcon color="green" variant="light" onClick={() => navigate({ to: `/dashboard/pedidos/$id/modal`, params: { id } })}>
                  <ClipboardTextIcon />
                </ActionIcon>
              </Tooltip>

              <RoleGuard notAllowed={[RoleGuard.Role.Transportadora]}>
                <Tooltip label="Editar Pedido">
                  <ActionIcon color="yellow" variant="light" onClick={() => navigate({ to: `/dashboard/pedidos/editar/$id/modal`, params: { id } })}>
                    <PencilLineIcon />
                  </ActionIcon>
                </Tooltip>

                {/* TODO reavaliar implementação do render do botão de deletar */}
                {canDelete && (
                  <Tooltip label="Deletar Pedido">
                    <ActionIcon onClick={() => MODAL_HANDLER.delete.order({ id })} color="red" variant="light">
                      <TrashIcon />
                    </ActionIcon>
                  </Tooltip>
                )}
              </RoleGuard>
            </ActionIcon.Group>
          );
        }
      }
    ],
    [role]
  );

  return (
    <Stack>
      <div className="flex items-center justify-between">
        <DashboardComponents.Title>Gerenciar Pedidos</DashboardComponents.Title>

        <div className="flex gap-2">
          <DashboardComponents.RefreshButton tooltipProps={{ label: "Atualizar Pedidos" }} onClick={refetch} loading={isFetching} />

          <Button disabled={!hasNextPage || isFetching} onClick={fetchNextPage}>
            Carregar Mais
          </Button>

          <RoleGuard notAllowed={[Role.Transportadora]}>
            <Tooltip label="Novo Pedido">
              <Link to="/dashboard/pedidos/cadastrar/modal">
                <ActionIcon size="xl">
                  <PlusIcon weight="bold" />
                </ActionIcon>
              </Link>
            </Tooltip>
          </RoleGuard>
        </div>
      </div>

      <DataGridBase
        rows={rows}
        columns={columns}
        loading={isFetching}
        columnVisibilityModel={{
          id: false
        }}
      />
    </Stack>
  );
}
